import { useRef, useState } from 'react';
import { assertOk, buildRequestHeaders } from '../services/httpClient';

const STREAM_TIMEOUT_MS = 5 * 60 * 1000;

const useStreamingSummary = () => {
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef(null);

  const parseSseEvent = (eventBlock) => {
    const lines = eventBlock.split('\n');
    let eventType = 'message';
    let dataPayload = '';

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataPayload += line.slice(5).trim();
      }
    }

    if (!dataPayload) {
      return null;
    }

    try {
      return { eventType, data: JSON.parse(dataPayload) };
    } catch {
      return { eventType, data: { text: dataPayload } };
    }
  };

  const streamContent = async (url, requestBody, onChunk, onStatus) => {
    setIsStreaming(true);
    abortControllerRef.current = new AbortController();
    const timeoutId = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }, STREAM_TIMEOUT_MS);
    
    try {
      const headers = await buildRequestHeaders();
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal,
      });

      await assertOk(response);

      if (!response.body) {
        throw new Error('Streaming response body is empty');
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        while (buffer.includes('\n\n')) {
          const separatorIndex = buffer.indexOf('\n\n');
          const eventBlock = buffer.slice(0, separatorIndex).trim();
          buffer = buffer.slice(separatorIndex + 2);

          if (!eventBlock) continue;

          const parsed = parseSseEvent(eventBlock);
          if (!parsed) continue;

          if (parsed.eventType === 'chunk') {
            accumulatedText += parsed.data?.text || '';
            onChunk(accumulatedText);
          } else if (parsed.eventType === 'status') {
            if (onStatus) {
              onStatus(parsed.data || {});
            }
          } else if (parsed.eventType === 'error') {
            throw new Error(parsed.data?.message || 'Streaming failed');
          }
        }

        // Backward-compatible fallback for plain-text streams.
        if (!chunk.includes('event:') && !chunk.includes('data:')) {
          accumulatedText += chunk;
          onChunk(accumulatedText);
          buffer = '';
        }
      }
      
      return accumulatedText;
      
    } catch (error) {
      if (error.name === 'AbortError') {
        return '';
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
      setIsStreaming(false);
    }
  };

  const cancelStream = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
    }
  };

  return {
    isStreaming,
    streamContent,
    setIsStreaming,
    cancelStream,
  };
};

export default useStreamingSummary;
